import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import ControlsHeader from "shared/ControlsHeader";
import SearchBar from "shared/SearchBar";
import Button from "shared/Button";
import { FilterColumn } from "shared/FilterColumn";
import DisclosureListItem from "DreamCast/Shared/DisclosureListItem";
import DisclosureDetails from "DreamCast/Shared/DisclosureDetails";
import DisclosurePublishCard from "DreamCast/Shared/DisclosurePublishCard";
import DisclosureContentSettings from "DreamCast/Shared/DisclosureContentSettings";
import MediaUsedOnSection from "DreamCast/Shared/MediaUsedOnSection";
import FileUploadSection from "DreamCast/Shared/FileUploadSection";
import MediaThumbnailField, {
  EMPTY_THUMBNAIL_STATE,
  thumbnailStateFromDetail,
} from "DreamCast/Shared/MediaThumbnailField";
import DisclosurePublishingBar, {
  DisclosurePublishingState,
} from "DreamCast/Shared/DisclosurePublishingBar";
import HistoryOverlay from "DreamCast/Shared/HistoryOverlay";
import SortModal from "DreamCast/Shared/SortModal";
import {
  createDreamcastDisclosure,
  fetchDreamcastDisclosureById,
  fetchDreamcastDisclosures,
  moveMediaTempArchivedFiles,
  moveMediaTempAudioFiles,
  moveMediaTempVideoFiles,
  updateDreamcastDisclosure,
  moveMediaTempImage,
} from "DreamCast/api/media";
import styles from "./Disclosures.module.css";

const SORT_OPTIONS = [
  { label: "Date: Newest", value: "date-newest" },
  { label: "Date: Oldest", value: "date-oldest" },
  { label: "Title: A to Z", value: "title-asc" },
  { label: "Title: Z to A", value: "title-desc" },
];

const FILTER_SECTIONS = [
  {
    label: "File Type",
    options: [
      { label: "Audio", value: "audio" },
      { label: "Video", value: "video" },
    ],
  },
  {
    label: "Language",
    options: [
      { label: "English", value: "english" },
      { label: "Spanish", value: "spanish" },
      { label: "Korean", value: "korean" },
    ],
  },
  {
    label: "Tag",
    options: [],
  },
  {
    label: "Status",
    options: [
      { label: "Published", value: "published" },
      { label: "In Review", value: "in-review" },
      { label: "Archived", value: "archived" },
    ],
  },
];

const fileStateKey = (items = []) =>
  JSON.stringify(
    items.map((item) => ({
      id: item.id,
      name: item.name,
      type: item.type,
      previewUrl: item.previewUrl,
      naCrNumber: item.naCrNumber,
      anzCrNumber: item.anzCrNumber,
    })),
  );

const withoutLocalPreviewUrl = (file) => {
  const persistedFile = { ...file };
  delete persistedFile.localPreviewUrl;
  if (/^blob:/i.test(String(persistedFile.previewUrl || ""))) {
    persistedFile.previewUrl = null;
  }
  return persistedFile;
};

const EMPTY_DISCLOSURE_TAGS = {
  selectedLocations: [],
  selectedLanguages: [],
  selectedOccupations: [],
  keywords: [],
  hiddenTags: [],
  byTypeCode: {},
};

const flattenSelectedTags = (tags = {}) =>
  Array.from(
    new Set(
      [
        tags.selectedLocations,
        tags.selectedLanguages,
        tags.selectedOccupations,
        tags.keywords,
        tags.hiddenTags,
        ...Object.values(tags.byTypeCode || {}),
      ]
        .filter(Array.isArray)
        .flat(),
    ),
  );

const formatHistoryList = (items, property) =>
  Array.isArray(items)
    ? items
        .map((item) => item?.[property])
        .filter(Boolean)
        .join("\n")
    : "";

const DISCLOSURE_HISTORY_FIELD_DEFS = [
  { key: "details.title", label: "Title", format: (value) => value || "" },
  {
    key: "details.description",
    label: "Description",
    format: (value) => value || "",
  },
  {
    key: "publishStatus",
    label: "Status",
    format: (value) => value || "",
  },
  {
    key: "tags",
    label: "Tags",
    format: (value) => flattenSelectedTags(value).join(", "),
  },
  {
    key: "thumbnail",
    label: "Thumbnail",
    format: (value) =>
      value?.thumbnail?.src
        ? value.builderData
          ? "Generated thumbnail"
          : "Uploaded image"
        : "",
  },
  {
    key: "files",
    label: "Files",
    format: (value) => formatHistoryList(value, "name"),
  },
  {
    key: "archivedFiles",
    label: "Archived Files",
    format: (value) => formatHistoryList(value, "name"),
  },
  {
    key: "mediaUsedOn",
    label: "Media Used On",
    format: (value) => formatHistoryList(value, "title"),
  },
];

const buildDisclosureSnapshot = ({
  details,
  publishStatus,
  tags,
  thumbnail = EMPTY_THUMBNAIL_STATE,
  files,
  archivedFiles,
  mediaUsedOn,
}) => ({
  details,
  publishStatus,
  tags,
  thumbnail: {
    thumbnail: thumbnail.thumbnail,
    builderData: thumbnail.builderData,
    builderOpen: thumbnail.builderOpen,
    tempFilename: thumbnail.tempFilename,
  },
  files,
  archivedFiles,
  mediaUsedOn,
});

const normalizeDisclosureListItem = (item) => ({
  ...item,
  id: item.id || item.mediaItemId,
  publishDate: item.publishDate || item.PublishOn || item.date || "",
  sortDate: item.publishDate || item.PublishOn || item.date || "",
  mediaUsedOn: Number(item.mediaUsedOn || 0),
  type:
    item.type ||
    (String(item.mediaTypeCode).toLowerCase() === "video" ? "Video" : "Audio"),
  language: String(item.language || "english").toLowerCase(),
  status: item.status || item.publishState?.status || "In Review",
  tags: Array.isArray(item.tags) ? item.tags : [],
  contentSettings: item.contentSettings || {},
});

export function Disclosures() {
  const pageRef = useRef(null);
  const history = useHistory();
  const [isCreateView, setIsCreateView] = useState(false);
  const [editingDisclosureId, setEditingDisclosureId] = useState(null);
  const [hasPendingRevisions, setHasPendingRevisions] = useState(false);
  const [historyBaseline, setHistoryBaseline] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [details, setDetails] = useState({ title: "", description: "" });
  const [files, setFiles] = useState([]);
  const [archivedFiles, setArchivedFiles] = useState([]);
  const [thumbnailState, setThumbnailState] = useState(EMPTY_THUMBNAIL_STATE);
  // Saved thumbnail URL, passed as oldUrl when a new one is moved into place.
  const [savedThumbnailUrl, setSavedThumbnailUrl] = useState("");
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const thumbnailFieldRef = useRef(null);
  const [publishStatus, setPublishStatus] = useState("In Review");
  const [disclosureTags, setDisclosureTags] = useState(EMPTY_DISCLOSURE_TAGS);
  const [mediaUsedOn, setMediaUsedOn] = useState([]);
  const [disclosures, setDisclosures] = useState([]);
  const [apiError, setApiError] = useState("");
  const [isPublishing, setIsPublishing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({ Status: ["published"] });
  const [sortBy, setSortBy] = useState("date-newest");

  const loadDisclosures = React.useCallback(async () => {
    const response = await fetchDreamcastDisclosures();
    const next = Array.isArray(response?.data) ? response.data : [];
    setDisclosures(next.map(normalizeDisclosureListItem));
    return next;
  }, []);

  useEffect(() => {
    let subscribed = true;
    fetchDreamcastDisclosures()
      .then((response) => {
        if (!subscribed) return;
        const next = Array.isArray(response?.data) ? response.data : [];
        setDisclosures(next.map(normalizeDisclosureListItem));
      })
      .catch((error) => {
        if (subscribed) {
          setApiError(error.message || "Failed to load disclosures.");
        }
      });
    return () => {
      subscribed = false;
    };
  }, []);

  const visibleDisclosures = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    const selectedTypes = filters["File Type"] || [];
    const selectedLanguages = filters.Language || [];
    const selectedTags = filters.Tag || [];
    const selectedStatuses = filters.Status || [];

    return disclosures
      .filter((disclosure) => {
        if (normalizedQuery) {
          const tagsString = Array.isArray(disclosure.tags)
            ? disclosure.tags.join(" ")
            : "";
          const hiddenTagsString = Array.isArray(disclosure.contentSettings?.tags?.hiddenTags)
            ? disclosure.contentSettings.tags.hiddenTags.join(" ")
            : "";
          const searchable = `${disclosure.title} ${tagsString} ${hiddenTagsString}`.toLowerCase();
          if (!searchable.includes(normalizedQuery)) {
            return false;
          }
        }

        if (
          selectedTypes.length &&
          !selectedTypes.includes(disclosure.type.toLowerCase())
        ) {
          return false;
        }

        if (
          selectedLanguages.length &&
          !selectedLanguages.includes(disclosure.language)
        ) {
          return false;
        }

        if (
          selectedTags.length &&
          !selectedTags.some((tag) => disclosure.tags.includes(tag))
        ) {
          return false;
        }

        return (
          !selectedStatuses.length ||
          selectedStatuses.includes(
            disclosure.status.toLowerCase().replace(" ", "-"),
          )
        );
      })
      .sort((left, right) => {
        if (sortBy === "date-oldest") {
          return left.sortDate.localeCompare(right.sortDate);
        }
        if (sortBy === "title-asc") {
          return left.title.localeCompare(right.title);
        }
        if (sortBy === "title-desc") {
          return right.title.localeCompare(left.title);
        }
        return right.sortDate.localeCompare(left.sortDate);
      });
  }, [disclosures, filters, searchQuery, sortBy]);

  const searchBarNoResults =
    visibleDisclosures.length === 0 &&
    String(searchQuery || "").trim() !== "";

  const closeCreateView = () => {
    setIsCreateView(false);
    setEditingDisclosureId(null);
    setHasPendingRevisions(false);
    setHistoryBaseline(null);
    setShowHistory(false);
    setDetails({ title: "", description: "" });
    setFiles([]);
    setArchivedFiles([]);
    setThumbnailState(EMPTY_THUMBNAIL_STATE);
    setSavedThumbnailUrl("");
    setPublishStatus("In Review");
    setDisclosureTags(EMPTY_DISCLOSURE_TAGS);
    setMediaUsedOn([]);
  };

  const openCreateView = () => {
    closeCreateView();
    setHistoryBaseline(
      buildDisclosureSnapshot({
        details: { title: "", description: "" },
        publishStatus: "In Review",
        tags: EMPTY_DISCLOSURE_TAGS,
        files: [],
        archivedFiles: [],
        mediaUsedOn: [],
      }),
    );
    setIsCreateView(true);
  };

  const openEditView = async (disclosure) => {
    setApiError("");
    const response = await fetchDreamcastDisclosureById(disclosure.id);
    const detail = response?.data || response;
    const nextDetails = {
      title: detail?.detailState?.title || disclosure.title,
      description: detail?.detailState?.descriptionValue || "",
    };
    const nextFiles = Array.isArray(detail?.files) ? detail.files : [];
    const nextArchivedFiles = Array.isArray(detail?.archivedFiles)
      ? detail.archivedFiles
      : [];
    const nextTags = {
      ...EMPTY_DISCLOSURE_TAGS,
      ...(detail?.contentSettings?.tags || {}),
    };
    const nextMediaUsedOn = Array.isArray(detail?.mediaUsedOn)
      ? detail.mediaUsedOn
      : [];
    const nextStatus = detail?.publishState?.status || disclosure.status;
    const nextThumbnail = thumbnailStateFromDetail(
      detail,
      `${nextDetails.title} thumbnail`,
    );

    setEditingDisclosureId(disclosure.id);
    setHasPendingRevisions(true);
    setDetails(nextDetails);
    setFiles(nextFiles);
    setArchivedFiles(nextArchivedFiles);
    setThumbnailState(nextThumbnail);
    setSavedThumbnailUrl(detail?.thumbnailPath || "");
    setPublishStatus(nextStatus);
    setDisclosureTags(nextTags);
    setMediaUsedOn(nextMediaUsedOn);
    setHistoryBaseline(
      buildDisclosureSnapshot({
        details: nextDetails,
        publishStatus: nextStatus,
        tags: nextTags,
        thumbnail: nextThumbnail,
        files: nextFiles,
        archivedFiles: nextArchivedFiles,
        mediaUsedOn: nextMediaUsedOn,
      }),
    );
    setIsCreateView(true);
  };

  const markRevisionPending = () => {
    if (editingDisclosureId) setHasPendingRevisions(true);
  };

  const currentHistorySnapshot = () =>
    buildDisclosureSnapshot({
      details,
      publishStatus,
      tags: disclosureTags,
      thumbnail: thumbnailState,
      files,
      archivedFiles,
      mediaUsedOn,
    });

  const restoreHistorySnapshot = (snapshot) => {
    if (!snapshot) return;
    setDetails(snapshot.details);
    setPublishStatus(snapshot.publishStatus);
    setDisclosureTags(snapshot.tags);
    if (snapshot.thumbnail) {
      setThumbnailState({ ...EMPTY_THUMBNAIL_STATE, ...snapshot.thumbnail });
      thumbnailFieldRef.current?.setBuilderData?.(snapshot.thumbnail.builderData);
    }
    setFiles(snapshot.files);
    setArchivedFiles(snapshot.archivedFiles);
    setMediaUsedOn(snapshot.mediaUsedOn);
    markRevisionPending();
  };

  const restoreHistoryField = (key, value) => {
    if (key === "details.title") {
      setDetails((current) => ({ ...current, title: value || "" }));
    } else if (key === "details.description") {
      setDetails((current) => ({ ...current, description: value || "" }));
    } else if (key === "publishStatus") {
      setPublishStatus(value);
    } else if (key === "tags") {
      setDisclosureTags(value);
    } else if (key === "thumbnail") {
      setThumbnailState({ ...EMPTY_THUMBNAIL_STATE, ...(value || {}) });
      thumbnailFieldRef.current?.setBuilderData?.(value?.builderData || null);
    } else if (key === "files") {
      setFiles(value);
    } else if (key === "archivedFiles") {
      setArchivedFiles(value);
    } else if (key === "mediaUsedOn") {
      setMediaUsedOn(value);
    }
    markRevisionPending();
  };

  const publishDisclosure = async () => {
    const title = details.title.trim();
    if (!title || isPublishing) return;
    if (thumbnailUploading) {
      setApiError("Please wait for the thumbnail upload to finish.");
      return;
    }

    setApiError("");
    setIsPublishing(true);
    try {
      const resolvedThumbnail =
        await thumbnailFieldRef.current.resolveForSave();
      const payload = {
        title,
        description: details.description,
        thumbnail: resolvedThumbnail.thumbnail?.src || "",
        thumbnailBuilderData: resolvedThumbnail.builderData || null,
        files: files.map(withoutLocalPreviewUrl),
        archivedFiles: archivedFiles.map(withoutLocalPreviewUrl),
        isDraft: publishStatus !== "Published",
        publishState: { status: publishStatus },
        contentSettings: { tags: disclosureTags },
      };

      const response = editingDisclosureId
        ? await updateDreamcastDisclosure(editingDisclosureId, payload)
        : await createDreamcastDisclosure(payload);
      const saved = response?.data || response;
      const savedId = editingDisclosureId || saved?.mediaItemId;
      const audioFilenames = files
        .filter((file) => file?.type === "Audio" && file?.tempFilename)
        .map((file) => file.tempFilename);
      const videoFilenames = files
        .filter((file) => file?.type === "Video" && file?.tempFilename)
        .map((file) => file.tempFilename);
      const archivedFilenames = archivedFiles
        .filter((file) => file?.tempFilename)
        .map((file) => file.tempFilename);

      await Promise.all([
        audioFilenames.length
          ? moveMediaTempAudioFiles(savedId, audioFilenames)
          : Promise.resolve(),
        videoFilenames.length
          ? moveMediaTempVideoFiles(savedId, videoFilenames)
          : Promise.resolve(),
        archivedFilenames.length
          ? moveMediaTempArchivedFiles(savedId, archivedFilenames)
          : Promise.resolve(),
        resolvedThumbnail.tempFilename
          ? moveMediaTempImage(
              savedId,
              resolvedThumbnail.tempFilename,
              savedThumbnailUrl || undefined,
            )
          : Promise.resolve(),
      ]);

      const detailResponse = await fetchDreamcastDisclosureById(savedId);
      const savedDetail = detailResponse?.data || detailResponse;
      const savedFiles = Array.isArray(savedDetail?.files)
        ? savedDetail.files
        : files;
      const savedArchivedFiles = Array.isArray(savedDetail?.archivedFiles)
        ? savedDetail.archivedFiles
        : archivedFiles;
      const savedThumbnail = thumbnailStateFromDetail(
        savedDetail,
        `${title} thumbnail`,
      );
      setFiles(savedFiles);
      setArchivedFiles(savedArchivedFiles);
      setThumbnailState(savedThumbnail);
      setSavedThumbnailUrl(savedDetail?.thumbnailPath || "");
      setEditingDisclosureId(savedId);
      setHasPendingRevisions(false);
      setHistoryBaseline(
        buildDisclosureSnapshot({
          details,
          publishStatus,
          tags: disclosureTags,
          thumbnail: savedThumbnail,
          files: savedFiles,
          archivedFiles: savedArchivedFiles,
          mediaUsedOn,
        }),
      );
      await loadDisclosures();
    } catch (error) {
      setApiError(error.message || "Failed to save disclosure.");
    } finally {
      setIsPublishing(false);
    }
  };

  const publishingState = editingDisclosureId
    ? hasPendingRevisions
      ? DisclosurePublishingState.editPending
      : DisclosurePublishingState.editSaved
    : DisclosurePublishingState.create;

  return (
    <div ref={pageRef} className={styles.page}>
      <ControlsHeader title="DreamCast - Disclosures" />

      {apiError && <div className={styles.apiError}>{apiError}</div>}

      {!isCreateView ? (
        <section className={styles.listLayout}>
          <div className={styles.toolbar}>
            <SearchBar
              placeholderText="Search Disclosures"
              value={searchQuery}
              onInput={setSearchQuery}
              minChar={0}
              searchDelay={0}
              noResults={searchBarNoResults}
            />
            <Button className={styles.addButton} onClick={openCreateView}>
              Add Disclosure
            </Button>
            <SortModal
              options={SORT_OPTIONS}
              selected={sortBy}
              onChange={setSortBy}
              container={pageRef}
            />
          </div>

          <div className={styles.listBody}>
            <FilterColumn
              className={styles.filterColumn}
              sections={FILTER_SECTIONS}
              selected={filters}
              onChange={setFilters}
            />

            <div className={styles.listContent}>
              <div className={styles.columnHeaders}>
                <span className={styles.colImage}>Image</span>
                <div className={styles.headerContent}>
                  <div className={styles.headerInfoCluster}>
                    <span className={styles.colTitle}>Title/Info</span>
                    <div className={styles.valueHeaders}>
                      <span className={styles.colMediaUsedOn}>Media Used On</span>
                      <span className={styles.colType}>Type</span>
                      <span className={styles.colStatus}>Status</span>
                    </div>
                  </div>
                  <span className={styles.headerActions} aria-hidden="true" />
                </div>
              </div>

              <div className={styles.listItems}>
                {visibleDisclosures.map((disclosure) => (
                  <DisclosureListItem
                    key={disclosure.id}
                    {...disclosure}
                    onEdit={() =>
                      openEditView(disclosure).catch((error) =>
                        setApiError(
                          error.message || "Failed to load disclosure.",
                        ),
                      )
                    }
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
      ) : (
        <section className={styles.createLayout}>
          {showHistory && historyBaseline && (
            <HistoryOverlay
              baseline={historyBaseline}
              current={currentHistorySnapshot()}
              fieldDefs={DISCLOSURE_HISTORY_FIELD_DEFS}
              onRestoreField={restoreHistoryField}
              onClose={() => setShowHistory(false)}
              onUndoAll={() => {
                restoreHistorySnapshot(historyBaseline);
                setShowHistory(false);
              }}
            />
          )}
          <DisclosurePublishingBar
            state={publishingState}
            isPublishing={isPublishing}
            publishDisabled={!details.title.trim() || thumbnailUploading}
            onBack={closeCreateView}
            onHistory={() => setShowHistory(true)}
            onPublish={publishDisclosure}
          />
          <div className={styles.formColumns}>
            <div className={styles.leftColumn}>
              <DisclosureDetails
                {...details}
                onChange={(field, value) => {
                  setDetails((current) => ({ ...current, [field]: value }));
                  markRevisionPending();
                }}
              />
              <MediaThumbnailField
                ref={thumbnailFieldRef}
                value={thumbnailState}
                onChange={(next) => {
                  setThumbnailState(next);
                  markRevisionPending();
                }}
                onUploadingChange={setThumbnailUploading}
                builderProps={{
                  detailFields: ["title"],
                  syncedDetails: { title: details.title },
                }}
              />
              <FileUploadSection
                showManagedFiles
                defaultFileType="Audio"
                fileTypeOptions={["Audio", "Video"]}
                initialFiles={files}
                initialArchivedFiles={archivedFiles}
                onFilesChange={({
                  files: nextFiles,
                  archivedFiles: nextArchived,
                }) => {
                  const filesChanged =
                    fileStateKey(nextFiles) !== fileStateKey(files);
                  const archivedChanged =
                    fileStateKey(nextArchived) !== fileStateKey(archivedFiles);

                  if (filesChanged || archivedChanged) {
                    setFiles(nextFiles);
                    setArchivedFiles(nextArchived);
                    markRevisionPending();
                  }
                }}
              />
            </div>
            <div className={styles.rightColumn}>
              <DisclosurePublishCard
                status={publishStatus}
                onChange={(nextStatus) => {
                  setPublishStatus(nextStatus);
                  markRevisionPending();
                }}
              />
              <DisclosureContentSettings
                tags={disclosureTags}
                onChange={(nextTags) => {
                  setDisclosureTags(nextTags);
                  markRevisionPending();
                }}
              />
              <MediaUsedOnSection
                media={mediaUsedOn}
                onEdit={(item) =>
                  history.push({
                    pathname: "/dreamcast/media",
                    state: { initialEditingAudioId: item.id },
                  })
                }
              />
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export default Disclosures;
